import { APICfg } from '../../../api'
import { uploadOrKeepImg } from '../../common'
import { addDaysToDayMonthLabel, formatISO } from 'services/utils/date'
import { DayMonthLabel } from 'types/common'
import { baseCustomerWrite } from '../pmi'
import { OrganizationToAPI } from './types/api/request-models'
import { APIOrganization } from './types/api/response-models'
import {
  BudgetDateRange,
  Organization,
} from './types/internal/organization-model'
import { parseImage } from '../../catalog/utils'

/**
 * In the monthDay dates we receive from the apis are only defined the values inserted in the from by the kampaayer.
 * Those are the start of the first range, and then the list of the ends of all the ranges,
 * which implicitly always start the day after the end of the previous range, as explained in https://gitlab.com/kampaay/general/-/issues/955
 *
 * @param apiModel is a list of DayMonth labels [A, B, C]
 * @returns for each date of the list returns a range object, which behaves as follows:
 *
 * A => { start: A, end: B }
 * B => { start: B + 1day, end: C }
 * C => { start: C + 1day, end: undefined }
 *
 * The last range, which always has `end: undefined` gets removed from the list
 */
const parseBudgetDateRange = (apiModel: DayMonthLabel[]) =>
  apiModel
    .map((date, idx, arr) => {
      const isFirst = idx === 0
      return {
        budgetSpentDateFrom: isFirst ? date : addDaysToDayMonthLabel(date, 1),
        budgetSpentDateTo: arr[idx + 1],
      }
    })
    .slice(0, -1)

/**
 * If the ranges is not defined or if the array is emptu, meaning that they have been removed from a product that had some
 * we then return undefined, to be compliant to the contract with BE explained here: https://gitlab.com/kampaay/general/-/issues/955#dto
 *
 * Else we map the ranges just picking the end for the ranges that are not the first one
 */
const writeBudgetDateRange = (ranges?: BudgetDateRange[]) => {
  if (!ranges?.length) return undefined

  return ranges?.flatMap(
    ({ budgetSpentDateFrom, budgetSpentDateTo }: BudgetDateRange, idx) =>
      idx === 0 ? [budgetSpentDateFrom, budgetSpentDateTo] : [budgetSpentDateTo]
  )
}

// CFG
const apiCfg: APICfg = {
  endpoint: 'organization',
  parse: ({
    budgetDateRange,
    image,
    members,
    ...res
  }: APIOrganization): Organization => {
    return {
      ...res,
      budgetDateRange: parseBudgetDateRange(budgetDateRange),
      image: parseImage(image),
      members: members,
      allowFullAddressCoverage: members.some(
        (m) => m.hasFullAddressCoverage === true
      ),
    }
  },
  write: async ({
    members,
    image: img,
    budgetDateRange,
    allowFullAddressCoverage,
    ...res
  }: Organization): Promise<OrganizationToAPI> => ({
    ...res,
    ...baseCustomerWrite(res),
    image: await uploadOrKeepImg(img),
    budgetDateRange: writeBudgetDateRange(budgetDateRange),
    members: members.map(
      ({ userId, role, budget, budgetDueDate, hasFullAddressCoverage }) => ({
        userId,
        role,
        budget: budget === 0 ? undefined : budget,
        budgetDueDate: formatISO(budgetDueDate),
        hasFullAddressCoverage: !!hasFullAddressCoverage,
      })
    ),
  }),
}

export default apiCfg
